import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useSubscriptionStore from '../store/subscriptionStore';
import { toast } from 'react-toastify';

// ---- Shared Styles ----
const cardStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(102, 126, 234, 0.2)',
  borderRadius: '16px',
  padding: '28px',
  marginBottom: '28px',
};

const sectionTitle = {
  fontSize: '1.15rem',
  fontWeight: '600',
  marginBottom: '20px',
  color: 'rgba(255,255,255,0.95)',
};

const gradientText = {
  background: 'linear-gradient(135deg, #667eea, #00d4ff)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const primaryButton = {
  padding: '12px 24px',
  background: 'linear-gradient(135deg, #667eea, #00d4ff)',
  border: 'none',
  borderRadius: '10px',
  color: 'white',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '0.9rem',
  transition: 'opacity 0.2s',
};

const secondaryButton = {
  padding: '10px 20px',
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '10px',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.9rem',
  transition: 'all 0.2s',
};

// ---- Sub-components ----

/** Status badge for subscription / invoice status */
const StatusBadge = ({ status }) => {
  const variants = {
    active: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', label: 'Active' },
    trialing: { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', label: 'Trial' },
    paused: { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', label: 'Paused' },
    cancelled: { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', label: 'Cancelled' },
    past_due: { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', label: 'Past Due' },
    paid: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', label: 'Paid' },
    pending: { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', label: 'Pending' },
    failed: { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', label: 'Failed' },
    refunded: { bg: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', label: 'Refunded' },
  };
  const v = variants[status] || variants.active;
  return (
    <span style={{
      padding: '4px 12px',
      background: v.bg,
      color: v.color,
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    }}>
      {v.label}
    </span>
  );
};

/** Progress bar for usage metrics */
const UsageBar = ({ label, used, limit, unit, color }) => {
  const isUnlimited = limit === -1 || limit === Infinity;
  const percentage = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const status = percentage >= 100 ? 'exceeded' : percentage >= 90 ? 'critical' : percentage >= 75 ? 'warning' : 'normal';

  const barColor = status === 'exceeded' || status === 'critical'
    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
    : status === 'warning'
      ? 'linear-gradient(135deg, #f59e0b, #d97706)'
      : color || 'linear-gradient(135deg, #667eea, #00d4ff)';

  const formatValue = (val) => {
    if (val === -1 || val === Infinity) return 'Unlimited';
    if (unit) return `${val} ${unit}`;
    return val.toLocaleString();
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
        fontSize: '0.9rem',
      }}>
        <span style={{ color: 'rgba(255,255,255,0.7)' }}>{label}</span>
        <span style={{ color: status === 'normal' ? '#00d4ff' : status === 'warning' ? '#f59e0b' : '#ef4444', fontWeight: '500' }}>
          {formatValue(used)} / {formatValue(limit)}
        </span>
      </div>
      <div style={{
        height: '8px',
        background: 'rgba(255,255,255,0.08)',
        borderRadius: '4px',
        overflow: 'hidden',
      }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: isUnlimited ? '0%' : `${percentage}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            height: '100%',
            background: barColor,
            borderRadius: '4px',
          }}
        />
      </div>
      {status === 'critical' && (
        <div style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: '4px' }}>
          You are approaching your limit. Consider upgrading your plan.
        </div>
      )}
      {status === 'exceeded' && (
        <div style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: '4px' }}>
          Limit exceeded. Overage charges may apply.
        </div>
      )}
    </div>
  );
};

/** Single plan card inside the plan comparison grid */
const PlanCard = ({ plan, isCurrent, isChanging, onSelect, billingCycle }) => {
  const displayPrice = billingCycle === 'annual' ? plan.annualPrice : plan.price;
  const isEnterprise = plan.key === 'enterprise';
  const isPopular = plan.isPopular;

  return (
    <motion.div
      whileHover={{ y: isCurrent ? 0 : -6 }}
      style={{
        padding: '24px 20px',
        background: isCurrent
          ? 'rgba(102, 126, 234, 0.18)'
          : isPopular
            ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.15), rgba(102, 126, 234, 0.15))'
            : 'rgba(255,255,255,0.02)',
        border: `2px solid ${isCurrent ? '#667eea' : isPopular ? '#9333ea' : 'rgba(102, 126, 234, 0.15)'}`,
        borderRadius: '16px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.2s',
        minWidth: 0,
      }}
    >
      {/* Badges */}
      {isCurrent && (
        <div style={{
          position: 'absolute',
          top: '-11px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#667eea',
          padding: '4px 14px',
          borderRadius: '12px',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          letterSpacing: '0.5px',
          color: 'white',
          whiteSpace: 'nowrap',
        }}>
          CURRENT PLAN
        </div>
      )}
      {isPopular && !isCurrent && (
        <div style={{
          position: 'absolute',
          top: '-11px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #9333ea, #667eea)',
          padding: '4px 14px',
          borderRadius: '12px',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          letterSpacing: '0.5px',
          color: 'white',
          whiteSpace: 'nowrap',
        }}>
          MOST POPULAR
        </div>
      )}

      <h3 style={{
        color: plan.color || '#00d4ff',
        fontFamily: 'Orbitron, monospace',
        fontSize: '0.95rem',
        marginBottom: '4px',
        marginTop: (isCurrent || isPopular) ? '6px' : '0',
      }}>
        {plan.name}
      </h3>

      <p style={{
        color: 'rgba(255,255,255,0.5)',
        fontSize: '0.78rem',
        lineHeight: '1.35',
        minHeight: '32px',
        marginBottom: '12px',
      }}>
        {plan.description}
      </p>

      <div style={{ fontSize: '1.9rem', fontWeight: 'bold', marginBottom: '2px', color: 'white' }}>
        {isEnterprise ? '$999' : `$${displayPrice}`}
        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', fontWeight: '400' }}>
          {isEnterprise ? '+' : ''}/mo
        </span>
      </div>

      {billingCycle === 'annual' && !isEnterprise && (
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', marginBottom: '12px' }}>
          Billed annually (${displayPrice * 12}/yr)
        </p>
      )}
      {billingCycle === 'monthly' && (
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', marginBottom: '12px' }}>
          Billed monthly
        </p>
      )}

      {/* Key features */}
      <div style={{
        borderTop: '1px solid rgba(102, 126, 234, 0.12)',
        paddingTop: '12px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '7px',
        fontSize: '0.8rem',
        marginBottom: '16px',
      }}>
        {(plan.features || []).slice(0, 6).map((feature, idx) => (
          <div key={idx} style={{ color: 'rgba(255,255,255,0.8)' }}>
            <span style={{ color: plan.color || '#00d4ff', marginRight: '6px' }}>{'\u2713'}</span>
            {feature}
          </div>
        ))}
        {(plan.features || []).length > 6 && (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
            +{plan.features.length - 6} more
          </div>
        )}
      </div>

      {/* CTA */}
      {isCurrent ? (
        <div style={{
          width: '100%',
          padding: '11px',
          background: 'rgba(102, 126, 234, 0.12)',
          border: '1px solid rgba(102, 126, 234, 0.25)',
          borderRadius: '8px',
          color: 'rgba(255,255,255,0.55)',
          fontWeight: '600',
          textAlign: 'center',
          fontSize: '0.85rem',
        }}>
          Current Plan
        </div>
      ) : (
        <button
          onClick={() => onSelect(plan.key)}
          disabled={isChanging}
          style={{
            width: '100%',
            padding: '11px',
            background: isPopular
              ? 'linear-gradient(135deg, #9333ea, #667eea)'
              : `linear-gradient(135deg, ${plan.color || '#667eea'}, #00d4ff)`,
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '600',
            cursor: isChanging ? 'wait' : 'pointer',
            fontSize: '0.85rem',
            opacity: isChanging ? 0.7 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          {isChanging ? 'Processing...' : plan.cta || 'Switch Plan'}
        </button>
      )}
    </motion.div>
  );
};

/** Cancellation / Pause flow modal */
const CancelFlowModal = ({ isOpen, step, onClose, onSetStep, onCancel, onPause }) => {
  const [cancelReason, setCancelReason] = useState('');
  const [pauseMonths, setPauseMonths] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const reasons = [
    'Too expensive',
    'Not using it enough',
    'Switching to a competitor',
    'Missing features I need',
    'Technical issues',
    'Other',
  ];

  const handleCancel = async () => {
    setIsProcessing(true);
    await onCancel(cancelReason);
    setIsProcessing(false);
  };

  const handlePause = async () => {
    setIsProcessing(true);
    await onPause(pauseMonths);
    setIsProcessing(false);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(10, 10, 15, 0.95)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        style={{
          background: 'rgba(15, 15, 20, 0.98)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '20px',
          padding: '36px',
          maxWidth: '520px',
          width: '100%',
          boxShadow: '0 0 40px rgba(239, 68, 68, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step 1: Reason */}
        {step === 1 && (
          <>
            <h2 style={{
              fontSize: '1.4rem',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: '#ef4444',
            }}>
              We are sorry to see you go
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '24px',
              lineHeight: '1.5',
              fontSize: '0.92rem',
            }}>
              Before you cancel, could you tell us why? Your feedback helps us improve.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {reasons.map((reason) => (
                <label
                  key={reason}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    background: cancelReason === reason ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${cancelReason === reason ? 'rgba(239, 68, 68, 0.4)' : 'rgba(255,255,255,0.08)'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason}
                    checked={cancelReason === reason}
                    onChange={() => setCancelReason(reason)}
                    style={{ accentColor: '#ef4444' }}
                  />
                  <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>{reason}</span>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={secondaryButton}>
                Keep My Plan
              </button>
              <button
                onClick={() => onSetStep(2)}
                disabled={!cancelReason}
                style={{
                  ...primaryButton,
                  background: cancelReason ? 'rgba(239, 68, 68, 0.8)' : 'rgba(239, 68, 68, 0.3)',
                  cursor: cancelReason ? 'pointer' : 'not-allowed',
                }}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* Step 2: Retention Offer */}
        {step === 2 && (
          <>
            <h2 style={{
              fontSize: '1.4rem',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: '#f59e0b',
            }}>
              Wait -- how about a special offer?
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '24px',
              lineHeight: '1.5',
              fontSize: '0.92rem',
            }}>
              We do not want to lose you. Here are some options before you go:
            </p>

            {/* Retention option 1: Discount */}
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(147, 51, 234, 0.1))',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '12px',
              marginBottom: '14px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '4px' }}>
                    Get 30% off for the next 3 months
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                    Keep all your features at a reduced price
                  </div>
                </div>
                <button
                  onClick={() => {
                    toast.success('Discount applied! 30% off for the next 3 months.');
                    onClose();
                  }}
                  style={{
                    ...primaryButton,
                    padding: '8px 18px',
                    fontSize: '0.82rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Apply Discount
                </button>
              </div>
            </div>

            {/* Retention option 2: Pause */}
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '12px',
              marginBottom: '14px',
            }}>
              <div style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '4px' }}>
                Pause your subscription instead
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', marginBottom: '12px' }}>
                Take a break without losing your data or settings
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <select
                  value={pauseMonths}
                  onChange={(e) => setPauseMonths(Number(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.85rem',
                  }}
                >
                  <option value={1}>1 month</option>
                  <option value={2}>2 months</option>
                  <option value={3}>3 months</option>
                </select>
                <button
                  onClick={handlePause}
                  disabled={isProcessing}
                  style={{
                    ...secondaryButton,
                    borderColor: 'rgba(251, 191, 36, 0.4)',
                    color: '#fbbf24',
                    cursor: isProcessing ? 'wait' : 'pointer',
                  }}
                >
                  {isProcessing ? 'Pausing...' : 'Pause Subscription'}
                </button>
              </div>
            </div>

            {/* Retention option 3: Downgrade */}
            <div style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: '12px',
              marginBottom: '24px',
            }}>
              <div style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '4px' }}>
                Downgrade to a smaller plan
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                Keep access at a lower price point. Switch plans in the comparison above.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => onSetStep(1)} style={secondaryButton}>
                Back
              </button>
              <button
                onClick={() => onSetStep(3)}
                style={{
                  ...primaryButton,
                  background: 'rgba(239, 68, 68, 0.7)',
                }}
              >
                I still want to cancel
              </button>
            </div>
          </>
        )}

        {/* Step 3: Final confirmation */}
        {step === 3 && (
          <>
            <h2 style={{
              fontSize: '1.4rem',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: '#ef4444',
            }}>
              Confirm Cancellation
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '12px',
              lineHeight: '1.5',
              fontSize: '0.92rem',
            }}>
              Your subscription will remain active until the end of your current billing period. After that:
            </p>
            <ul style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.88rem',
              lineHeight: '1.8',
              paddingLeft: '20px',
              marginBottom: '24px',
            }}>
              <li>You will lose access to all premium features</li>
              <li>Your data will be retained for 30 days</li>
              <li>You can reactivate at any time</li>
              <li>No further charges will be made</li>
            </ul>

            <div style={{
              padding: '16px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '10px',
              marginBottom: '24px',
              fontSize: '0.85rem',
              color: 'rgba(255,255,255,0.7)',
            }}>
              <strong style={{ color: '#ef4444' }}>Reason:</strong> {cancelReason}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={secondaryButton}>
                Keep My Plan
              </button>
              <button
                onClick={handleCancel}
                disabled={isProcessing}
                style={{
                  ...primaryButton,
                  background: '#ef4444',
                  cursor: isProcessing ? 'wait' : 'pointer',
                  opacity: isProcessing ? 0.7 : 1,
                }}
              >
                {isProcessing ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

// ---- Main Billing Page ----

const Billing = () => {
  const {
    currentPlan,
    subscriptionStatus,
    renewalDate,
    billingCycle,
    subscribedSince,
    paymentMethod,
    usageData,
    invoices,
    plans,
    isChangingPlan,
    isCancelFlowOpen,
    cancelStep,
    getCurrentPlanConfig,
    getUsagePercentage,
    changePlan,
    downloadInvoice,
    openCancelFlow,
    closeCancelFlow,
    setCancelStep,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    setBillingCycle,
  } = useSubscriptionStore();

  const [showAllPlans, setShowAllPlans] = useState(false);
  const [showPaymentUpdate, setShowPaymentUpdate] = useState(false);

  const planConfig = getCurrentPlanConfig();

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handlePlanChange = useCallback(async (planKey) => {
    await changePlan(planKey);
  }, [changePlan]);

  const handleManagePayment = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to manage your payment method');
        return;
      }

      const response = await fetch('https://quantrolix-backend-v2-production.up.railway.app/api/payments/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ return_url: window.location.href }),
      });

      if (!response.ok) throw new Error('Failed to create portal session');

      const data = await response.json();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.info('Payment portal is currently in demo mode.');
    }
  }, []);

  return (
    <div style={{ padding: '30px', maxWidth: '1200px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          marginBottom: '8px',
          fontFamily: 'Orbitron, monospace',
          ...gradientText,
        }}>
          Subscription Management
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem' }}>
          Manage your plan, usage, payment method, and billing history
        </p>
      </div>

      {/* ---- Section A: Current Plan Overview ---- */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.12), rgba(147, 51, 234, 0.1))',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '28px',
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '20px',
        }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px',
            }}>
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>Current Plan</span>
              <StatusBadge status={subscriptionStatus} />
            </div>

            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              marginBottom: '6px',
              fontFamily: 'Orbitron, monospace',
              color: planConfig?.color || '#00d4ff',
            }}>
              {planConfig?.name || 'Free'}
            </h2>

            <div style={{ fontSize: '2.2rem', fontWeight: 'bold', marginBottom: '16px', color: 'white' }}>
              ${planConfig?.price || 0}
              <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.45)', fontWeight: '400' }}>/month</span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '10px',
              fontSize: '0.88rem',
            }}>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: '8px' }}>Renewal:</span>
                {formatDate(renewalDate)}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: '8px' }}>Member since:</span>
                {formatDate(subscribedSince)}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: '8px' }}>Billing:</span>
                {billingCycle === 'annual' ? 'Annual' : 'Monthly'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: '8px' }}>Scans:</span>
                {planConfig?.scans === -1 ? 'Unlimited' : planConfig?.scans?.toLocaleString()}
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            minWidth: '160px',
          }}>
            <button
              onClick={() => setShowAllPlans(!showAllPlans)}
              style={primaryButton}
            >
              {showAllPlans ? 'Hide Plans' : 'Change Plan'}
            </button>
            {subscriptionStatus === 'paused' && (
              <button
                onClick={resumeSubscription}
                style={{
                  ...secondaryButton,
                  borderColor: 'rgba(34, 197, 94, 0.4)',
                  color: '#22c55e',
                }}
              >
                Resume Subscription
              </button>
            )}
            {subscriptionStatus === 'cancelled' && (
              <button
                onClick={resumeSubscription}
                style={{
                  ...secondaryButton,
                  borderColor: 'rgba(34, 197, 94, 0.4)',
                  color: '#22c55e',
                }}
              >
                Reactivate
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* ---- Section B: Plan Comparison (toggled) ---- */}
      <AnimatePresence>
        {showAllPlans && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden', marginBottom: '28px' }}
          >
            <div style={{
              ...cardStyle,
              marginBottom: 0,
              background: 'rgba(10, 10, 20, 0.6)',
              border: '1px solid rgba(102, 126, 234, 0.25)',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: '16px',
              }}>
                <h3 style={{
                  ...sectionTitle,
                  marginBottom: 0,
                  fontFamily: 'Orbitron, monospace',
                  ...gradientText,
                }}>
                  Compare Plans
                </h3>

                {/* Billing cycle toggle */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <span style={{
                    color: billingCycle === 'monthly' ? '#00d4ff' : 'rgba(255,255,255,0.45)',
                    fontSize: '0.88rem',
                    fontWeight: billingCycle === 'monthly' ? '600' : '400',
                  }}>
                    Monthly
                  </span>
                  <button
                    onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                    style={{
                      position: 'relative',
                      width: '52px',
                      height: '28px',
                      borderRadius: '14px',
                      border: 'none',
                      background: billingCycle === 'annual'
                        ? 'linear-gradient(135deg, #9333ea, #667eea)'
                        : 'rgba(255,255,255,0.12)',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'background 0.3s',
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '3px',
                      left: billingCycle === 'annual' ? '27px' : '3px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: 'white',
                      transition: 'left 0.3s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                  <span style={{
                    color: billingCycle === 'annual' ? '#00d4ff' : 'rgba(255,255,255,0.45)',
                    fontSize: '0.88rem',
                    fontWeight: billingCycle === 'annual' ? '600' : '400',
                  }}>
                    Annual
                  </span>
                  {billingCycle === 'annual' && (
                    <span style={{
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      color: 'white',
                      padding: '3px 10px',
                      borderRadius: '16px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}>
                      Save 20%
                    </span>
                  )}
                </div>
              </div>

              {/* Plan cards grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
                gap: '16px',
              }}>
                {plans.map((plan) => (
                  <PlanCard
                    key={plan.key}
                    plan={plan}
                    isCurrent={plan.key === currentPlan}
                    isChanging={isChangingPlan}
                    onSelect={handlePlanChange}
                    billingCycle={billingCycle}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- Section C: Usage Metrics ---- */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={cardStyle}
      >
        <h3 style={sectionTitle}>Usage This Period</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
        }}>
          <div style={{
            padding: '20px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '12px',
          }}>
            <UsageBar
              label="Product Scans"
              used={usageData.scans.used}
              limit={usageData.scans.limit}
              color="linear-gradient(135deg, #667eea, #00d4ff)"
            />
            <UsageBar
              label="API Calls"
              used={usageData.apiCalls.used}
              limit={usageData.apiCalls.limit}
              color="linear-gradient(135deg, #9333ea, #667eea)"
            />
          </div>
          <div style={{
            padding: '20px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '12px',
          }}>
            <UsageBar
              label="Storage"
              used={usageData.storage.used}
              limit={usageData.storage.limit}
              unit={usageData.storage.unit}
              color="linear-gradient(135deg, #14b8a6, #06b6d4)"
            />
            <UsageBar
              label="Team Members"
              used={usageData.teamMembers.used}
              limit={usageData.teamMembers.limit}
              color="linear-gradient(135deg, #f59e0b, #ef4444)"
            />
          </div>
        </div>
        <div style={{
          marginTop: '14px',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.4)',
        }}>
          Usage resets on {formatDate(usageData.scans.resetDate)}
        </div>
      </motion.div>

      {/* ---- Section D: Payment Method ---- */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        style={cardStyle}
      >
        <h3 style={sectionTitle}>Payment Method</h3>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '18px',
          background: 'rgba(102, 126, 234, 0.08)',
          borderRadius: '12px',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Card icon */}
            <div style={{
              width: '52px',
              height: '34px',
              background: 'linear-gradient(135deg, #1a1f71, #00d4ff)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              color: 'white',
            }}>
              {paymentMethod.brand?.toUpperCase() || 'CARD'}
            </div>
            <div>
              <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                {'\u2022\u2022\u2022\u2022'} {'\u2022\u2022\u2022\u2022'} {'\u2022\u2022\u2022\u2022'} {paymentMethod.last4}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem' }}>
                Expires {String(paymentMethod.expMonth).padStart(2, '0')}/{paymentMethod.expYear}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleManagePayment}
              style={{
                ...secondaryButton,
                borderColor: 'rgba(102, 126, 234, 0.3)',
                color: '#667eea',
                fontSize: '0.85rem',
                padding: '8px 16px',
              }}
            >
              Update Card
            </button>
            <button
              onClick={() => toast.info('Payment history exported to your email.')}
              style={{
                ...secondaryButton,
                borderColor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.85rem',
                padding: '8px 16px',
              }}
            >
              Export History
            </button>
          </div>
        </div>
      </motion.div>

      {/* ---- Section E: Invoices Table ---- */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          ...cardStyle,
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid rgba(102, 126, 234, 0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ ...sectionTitle, marginBottom: 0 }}>Invoices</h3>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
          </span>
        </div>

        {invoices.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.4)',
          }}>
            No invoices yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(102, 126, 234, 0.06)' }}>
                  {['Invoice', 'Date', 'Description', 'Amount', 'Status', ''].map((h) => (
                    <th key={h} style={{
                      padding: '12px 20px',
                      textAlign: 'left',
                      color: 'rgba(255,255,255,0.5)',
                      fontWeight: '500',
                      fontSize: '0.82rem',
                      whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr
                    key={inv.id}
                    style={{
                      borderBottom: idx < invoices.length - 1 ? '1px solid rgba(102, 126, 234, 0.08)' : 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.04)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '14px 20px', fontSize: '0.88rem', color: '#00d4ff', fontFamily: 'monospace' }}>
                      {inv.id}
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
                      {formatDate(inv.date)}
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)' }}>
                      {inv.description}
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: '0.88rem', color: 'white', fontWeight: '500' }}>
                      ${inv.amount.toFixed(2)}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <StatusBadge status={inv.status} />
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <button
                        onClick={() => downloadInvoice(inv.id)}
                        style={{
                          padding: '6px 14px',
                          background: 'rgba(102, 126, 234, 0.08)',
                          border: '1px solid rgba(102, 126, 234, 0.25)',
                          borderRadius: '6px',
                          color: '#667eea',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(102, 126, 234, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)';
                        }}
                      >
                        Download PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* ---- Section F: Cancel / Pause Subscription ---- */}
      {subscriptionStatus === 'active' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          style={{
            ...cardStyle,
            background: 'rgba(239, 68, 68, 0.04)',
            border: '1px solid rgba(239, 68, 68, 0.15)',
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <h3 style={{
                ...sectionTitle,
                marginBottom: '6px',
                color: '#ef4444',
              }}>
                Cancel or Pause Subscription
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem' }}>
                Need a break? You can pause your subscription or cancel it entirely.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  pauseSubscription(1);
                }}
                style={{
                  ...secondaryButton,
                  borderColor: 'rgba(251, 191, 36, 0.35)',
                  color: '#fbbf24',
                }}
              >
                Pause 1 Month
              </button>
              <button
                onClick={openCancelFlow}
                style={{
                  ...secondaryButton,
                  borderColor: 'rgba(239, 68, 68, 0.35)',
                  color: '#ef4444',
                }}
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Paused state banner */}
      {subscriptionStatus === 'paused' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          style={{
            ...cardStyle,
            background: 'rgba(251, 191, 36, 0.06)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <h3 style={{
                ...sectionTitle,
                marginBottom: '6px',
                color: '#fbbf24',
              }}>
                Subscription Paused
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem' }}>
                Your subscription is currently paused. Your data is safe and you can resume at any time.
              </p>
            </div>
            <button
              onClick={resumeSubscription}
              style={{
                ...primaryButton,
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              }}
            >
              Resume Subscription
            </button>
          </div>
        </motion.div>
      )}

      {/* Cancelled state banner */}
      {subscriptionStatus === 'cancelled' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          style={{
            ...cardStyle,
            background: 'rgba(239, 68, 68, 0.06)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <h3 style={{
                ...sectionTitle,
                marginBottom: '6px',
                color: '#ef4444',
              }}>
                Subscription Cancelled
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem' }}>
                Your subscription is cancelled. You retain access until {formatDate(renewalDate)}.
              </p>
            </div>
            <button
              onClick={resumeSubscription}
              style={{
                ...primaryButton,
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              }}
            >
              Reactivate Subscription
            </button>
          </div>
        </motion.div>
      )}

      {/* Cancel Flow Modal */}
      <AnimatePresence>
        <CancelFlowModal
          isOpen={isCancelFlowOpen}
          step={cancelStep}
          onClose={closeCancelFlow}
          onSetStep={setCancelStep}
          onCancel={cancelSubscription}
          onPause={pauseSubscription}
        />
      </AnimatePresence>
    </div>
  );
};

export default Billing;
